import { NavLink } from 'react-router-dom'
import { ROUTES } from '@shared/constants'
import { useThemeStore } from '@/stores/theme'
import { useAuth } from '@/hooks/useAuth'
import { useAlertsUnread } from '@/hooks/useAlertsUnread'
import { Icon } from '@/components/ui/Icon'
import { CXAdminMark, CXAdminWordmark, Avatar } from '@/components/ui/Logo'

const NAV = [
  {
    group: 'Operate',
    items: [
      { id: 'dashboard',     label: 'Dashboard',     icon: 'dashboard' as const, to: ROUTES.DASHBOARD,     live: false },
      { id: 'livewall',      label: 'Live Wall',      icon: 'grid'      as const, to: ROUTES.LIVE_WALL,     live: true  },
      { id: 'conversations', label: 'Conversations',  icon: 'users'     as const, to: ROUTES.CONVERSATIONS, live: false },
      { id: 'alerts',        label: 'Alerts',         icon: 'bell'      as const, to: ROUTES.ALERTS,        live: false },
      { id: 'logs',          label: 'Logs',           icon: 'logs'      as const, to: ROUTES.LOGS,          live: true  },
    ],
  },
  {
    group: 'Build',
    items: [
      { id: 'knowledge', label: 'Knowledge Base', icon: 'book'   as const, to: ROUTES.KNOWLEDGE_BASE, live: false },
      { id: 'flows',     label: 'Flows',          icon: 'flow'   as const, to: ROUTES.FLOWS,          live: false },
      { id: 'prompts',   label: 'Prompts',        icon: 'prompt' as const, to: ROUTES.PROMPTS,        live: false },
      { id: 'rag',       label: 'Retrieval',      icon: 'layers' as const, to: ROUTES.RETRIEVAL,      live: false },
      { id: 'crm',       label: 'CRM',            icon: 'users'  as const, to: ROUTES.CRM,            live: false },
      { id: 'llm',       label: 'LLM Config',     icon: 'bot'    as const, to: ROUTES.LLM_CONFIG,     live: false },
    ],
  },
  {
    group: 'Customize',
    items: [
      { id: 'style',      label: 'Voice & Style', icon: 'sparkle' as const, to: ROUTES.VOICE_STYLE, live: false },
      { id: 'appearance', label: 'Appearance',    icon: 'palette' as const, to: ROUTES.APPEARANCE,  live: false },
    ],
  },
]

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useThemeStore()
  const { user } = useAuth()
  const { data: alertsUnread = 0 } = useAlertsUnread()
  const collapsed = sidebarCollapsed

  return (
    <aside style={{
      borderRight: '1px solid var(--line)',
      background: 'rgba(255,255,255,.96)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Logo + collapse toggle */}
      <div style={{ minHeight: 99, padding: collapsed ? '18px 14px' : '20px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {collapsed ? <CXAdminMark size={42} /> : <CXAdminWordmark size={50} />}
        <button className="btn ghost icon sm" onClick={toggleSidebar} style={{ flexShrink: 0, display: collapsed ? 'none' : undefined }} aria-label="Collapse sidebar">
          <Icon name={collapsed ? 'arrowRight' : 'arrowLeft'} size={14} />
        </button>
      </div>

      {/* Search shortcut */}
      <div style={{ padding: collapsed ? '4px 12px 12px' : '4px 22px 12px' }}>
        <button className="btn" style={{
          width: '100%',
          justifyContent: collapsed ? 'center' : 'flex-start',
          background: 'white',
          borderColor: 'var(--line-strong)',
          borderRadius: 10,
          padding: collapsed ? '10px' : '10px 12px',
        }}>
          <Icon name="search" size={14} />
          {!collapsed && (
            <>
              <span style={{ flex: 1, textAlign: 'start', color: 'var(--ink-3)', fontSize: 13 }}>Search…</span>
              <kbd>⌘K</kbd>
            </>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: collapsed ? '8px' : '8px 16px' }}>
        {NAV.map(group => (
          <div key={group.group} style={{ marginBottom: 14 }}>
            {!collapsed && (
              <div style={{
                fontSize: 10.5, fontWeight: 600, color: 'var(--ink-3)',
                textTransform: 'uppercase', letterSpacing: '.05em',
                padding: '8px 8px 5px',
              }}>
                {group.group}
              </div>
            )}
            {group.items.map(item => {
              const badgeCount = item.id === 'alerts' ? alertsUnread : 0
              return (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.to === ROUTES.DASHBOARD}
                style={{ display: 'block', textDecoration: 'none' }}
              >
                {({ isActive }) => (
                  <div
                    data-tip={collapsed ? item.label : undefined}
                    style={{
                      position: 'relative',
                      width: '100%',
                      padding: collapsed ? 10 : '9px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      borderRadius: 9,
                      marginBottom: 2,
                      background: isActive ? 'linear-gradient(90deg, rgba(39,103,255,.11), rgba(39,103,255,.04))' : 'transparent',
                      color: isActive ? 'var(--brand-blue)' : 'var(--ink-2)',
                      border: '1px solid ' + (isActive ? 'rgba(39,103,255,.04)' : 'transparent'),
                      fontSize: 13,
                      fontWeight: isActive ? 500 : 400,
                      cursor: 'pointer',
                      transition: 'all .15s var(--ease-out)',
                    }}
                  >
                    {isActive && !collapsed && <span style={{ position: 'absolute', left: -16, top: 6, bottom: 6, width: 3, borderRadius: '0 3px 3px 0', background: 'var(--brand-blue)' }} />}
                    <Icon name={item.icon} size={17} stroke={isActive ? 2 : 1.7} />
                    {/* Collapsed: tiny corner dot carrying the count */}
                    {collapsed && badgeCount > 0 && (
                      <span style={{
                        position: 'absolute', top: 4, right: 4,
                        minWidth: 15, height: 15, padding: '0 4px',
                        borderRadius: 99, background: 'var(--danger, #ef4444)',
                        color: '#fff', fontSize: 9.5, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        lineHeight: 1,
                      }}>{badgeCount > 99 ? '99+' : badgeCount}</span>
                    )}
                    {!collapsed && (
                      <>
                        <span style={{ flex: 1, textAlign: 'start' }}>{item.label}</span>
                        {badgeCount > 0 && (
                          <span style={{
                            minWidth: 18, height: 18, padding: '0 5px',
                            borderRadius: 99, background: 'var(--danger, #ef4444)',
                            color: '#fff', fontSize: 10.5, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            lineHeight: 1,
                          }}>{badgeCount > 99 ? '99+' : badgeCount}</span>
                        )}
                        {item.live && badgeCount === 0 && (
                          <span style={{
                            width: 6, height: 6, borderRadius: 99,
                            background: 'var(--ok)',
                            animation: 'pulse 1.4s infinite',
                          }} />
                        )}
                      </>
                    )}
                  </div>
                )}
              </NavLink>
            )})}
          </div>
        ))}
      </nav>

      {/* User section */}
      <div style={{ borderTop: '1px solid var(--line)', padding: collapsed ? 12 : '14px 22px' }}>
        {collapsed ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Avatar name={user?.name ?? 'U'} size={38} gradient />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar name={user?.name ?? 'U'} size={38} gradient />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                {user?.name ?? 'Admin'}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                {user?.email ?? 'Administrator'}
              </div>
            </div>
            <button className="btn ghost icon sm" aria-label="Account settings">
              <Icon name="chevronDown" size={14} />
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}
